// Country data utilities for PhoneInput component.
// Bridges libphonenumber-js with the app's Select component format.

import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

/** Convert a 2-letter ISO country code to a Unicode flag emoji. */
export function countryToEmoji(code: string): string {
  return code
    .toUpperCase()
    .split('')
    .map((ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65))
    .join('');
}

/** Countries shown at the top of the dropdown for quick access. */
const PRIORITY_COUNTRIES: CountryCode[] = ['US', 'CA', 'GB', 'AU', 'MX'];

/**
 * Intl.DisplayNames provides localized country names for all ISO 3166 codes
 * with zero additional bundle size (built into modern browsers and Node 12+).
 */
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

/** Get the display name for a country code, falling back to the code itself. */
export function getCountryName(code: CountryCode): string {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
}

export interface CountryOption {
  value: string;
  label: string;
  dialCode: string;
  flag: string;
  isGroupHeader?: boolean;
}

/** Build the full list of country options for the Select dropdown. */
function buildCountryOptions(): CountryOption[] {
  const allCodes = getCountries();
  const options: CountryOption[] = [];

  // Priority countries at top
  options.push({ value: '__priority_header', label: 'Common', dialCode: '', flag: '', isGroupHeader: true });
  for (const code of PRIORITY_COUNTRIES) {
    const dialCode = `+${getCountryCallingCode(code)}`;
    options.push({
      value: code,
      label: `${countryToEmoji(code)} ${getCountryName(code)} (${dialCode})`,
      dialCode,
      flag: countryToEmoji(code),
    });
  }

  // All other countries alphabetically
  options.push({ value: '__all_header', label: 'All countries', dialCode: '', flag: '', isGroupHeader: true });
  const remaining = allCodes
    .filter((c) => !PRIORITY_COUNTRIES.includes(c))
    .map((code) => ({
      code,
      name: getCountryName(code),
      dialCode: `+${getCountryCallingCode(code)}`,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  for (const { code, name, dialCode } of remaining) {
    options.push({
      value: code,
      label: `${countryToEmoji(code)} ${name} (${dialCode})`,
      dialCode,
      flag: countryToEmoji(code),
    });
  }

  return options;
}

/** Pre-built list of country options for the PhoneInput dropdown. */
export const COUNTRY_OPTIONS = buildCountryOptions();

export const DEFAULT_COUNTRY: CountryCode = 'US';

export type { CountryCode };
